import * as React from "react";

/** Public types */
export type SwipeDirection = "left" | "right";
export type DeckItem = { id: string } & Record<string, any>;

export type SwipeDeckProps = {
  /** Items to render. Each must have a unique `id` */
  items: DeckItem[];

  /** Render function for a single card (full-bleed). You provide the inner layout. */
  renderCard: (item: DeckItem) => React.ReactNode;

  /** Called when a card is swiped past threshold */
  onSwipe?: (direction: SwipeDirection, item: DeckItem) => void;

  /** Called when the deck becomes empty (loop=false) */
  onEmpty?: () => void;

  /** Optional: pull-to-refresh handler or a simple refresh button substitute */
  onRefresh?: () => void | Promise<any>;

  /** If true, swiped cards rotate to the back. If false, they are removed. Default: true */
  loop?: boolean;

  /** Pixel distance to trigger a swipe. Default: 120px */
  swipeDistance?: number;

  /** Optional class on outer wrapper */
  className?: string;
};

/**
 * A small, dependency-free swipe deck:
 * - Touch & mouse support
 * - Thresholded horizontal swipe
 * - Reduce motion aware
 * - Works with SSR (no window access during render)
 */
export default function SwipeDeck({
  items,
  renderCard,
  onSwipe,
  onEmpty,
  onRefresh,
  loop = true,
  swipeDistance = 120,
  className,
}: SwipeDeckProps) {
  const [ordered, setOrdered] = React.useState<DeckItem[]>(items);
  const [dragX, setDragX] = React.useState(0);
  const [swiping, setSwiping] = React.useState(false);
  const [thresholdDir, setThresholdDir] = React.useState<SwipeDirection | null>(null);

  // Desktop mouse state
  const pointerDownRef = React.useRef(false);
  const pointerStartXRef = React.useRef(0);

  // Reduce-motion awareness
  const [reduceMotion, setReduceMotion] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === "undefined" || !("matchMedia" in window)) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduceMotion(!!mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  // Keep deck stable when items prop changes:
  // - If current top card id still exists, keep it on top and rebuild the rest below it.
  // - Otherwise reset to new items.
  React.useEffect(() => {
    setOrdered((prev) => {
      if (!prev.length) return items;
      const top = prev[0];
      if (!top || !items.some((i) => i.id === top.id)) return items;
      const next = [top, ...items.filter((i) => i.id !== top.id)];
      return next;
    });
  }, [items]);

  // Keyboard (left/right arrows) for desktop convenience
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!ordered.length) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        finalizeSwipe("right");
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        finalizeSwipe("left");
      }
    };
    if (typeof window !== "undefined") {
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }
  }, [ordered]);

  const active = ordered[0] || null;

  function rotateForward() {
    if (!ordered.length) return;
    if (loop) {
      setOrdered((cur) => {
        const [first, ...rest] = cur;
        return [...rest, first];
      });
    } else {
      setOrdered((cur) => {
        const [, ...rest] = cur;
        const emptyAfter = rest.length === 0;
        if (emptyAfter) queueMicrotask(() => onEmpty?.());
        return rest;
      });
    }
  }

  function animateOut(direction: SwipeDirection, onDone: () => void) {
    const card = document.getElementById("sg-swipe-active");
    if (!card) {
      onDone();
      return;
    }
    if (reduceMotion) {
      onDone();
      return;
    }
    const width = card.offsetWidth || (typeof window !== "undefined" ? window.innerWidth : 320);
    const targetX = direction === "left" ? -width * 1.2 : width * 1.2;
    card.style.transition = "transform 180ms ease-out, opacity 180ms ease-out, box-shadow 140ms ease";
    requestAnimationFrame(() => {
      card.style.transform = `translate3d(${targetX}px,0,0)`;
      card.style.opacity = "0";
      card.style.boxShadow = "0 10px 32px -8px rgba(0,0,0,0.55)";
    });
    setTimeout(() => {
      // reset
      card.style.transition = "";
      card.style.transform = "";
      card.style.opacity = "";
      card.style.boxShadow = "";
      onDone();
    }, 190);
  }

  function resetSwipe() {
    const card = document.getElementById("sg-swipe-active");
    if (card) {
      if (!reduceMotion) {
        card.style.transition = "transform 140ms ease, box-shadow 140ms ease";
      }
      requestAnimationFrame(() => {
        card.style.transform = "translate3d(0,0,0)";
        card.style.boxShadow = "";
      });
      if (!reduceMotion) {
        setTimeout(() => {
          if (card) card.style.transition = "";
        }, 150);
      } else {
        card.style.transition = "";
      }
    }
    setDragX(0);
    setSwiping(false);
    setThresholdDir(null);
  }

  function finalizeSwipe(direction: SwipeDirection) {
    if (!active) return;
    animateOut(direction, () => {
      try {
        onSwipe?.(direction, active);
      } catch {}
      rotateForward();
      setDragX(0);
      setSwiping(false);
      setThresholdDir(null);
    });
  }

  // Touch handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!active) return;
    if (e.touches.length !== 1) return;
    setSwiping(true);
    setDragX(0);
    setThresholdDir(null);
    const card = document.getElementById("sg-swipe-active");
    if (card) card.style.willChange = "transform, box-shadow";
    (e.currentTarget as any)._sgStartX = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!active || !swiping || e.touches.length !== 1) return;
    const startX = (e.currentTarget as any)._sgStartX ?? e.touches[0].clientX;
    if ((e.currentTarget as any)._sgStartX == null) (e.currentTarget as any)._sgStartX = startX;
    const rel = e.touches[0].clientX - startX;
    setDragX(rel);
    advanceProgress(rel);
  };

  const handleTouchEnd = () => {
    const cardEl = document.getElementById("sg-swipe-active") as any;
    if (cardEl && cardEl._sgStartX != null) delete cardEl._sgStartX;
    if (!swiping) return;
    if (Math.abs(dragX) >= swipeDistance) {
      finalizeSwipe(dragX > 0 ? "right" : "left");
    } else {
      resetSwipe();
    }
  };

  // Mouse handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!active) return;
    if (e.button !== 0) return;
    pointerDownRef.current = true;
    pointerStartXRef.current = e.clientX;
    setSwiping(true);
    setDragX(0);
    setThresholdDir(null);
    const card = document.getElementById("sg-swipe-active");
    if (card) card.style.willChange = "transform, box-shadow";
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!pointerDownRef.current || !active) return;
    const rel = e.clientX - pointerStartXRef.current;
    setDragX(rel);
    advanceProgress(rel);
  };

  const handleMouseUp = () => {
    if (!pointerDownRef.current) return;
    pointerDownRef.current = false;
    if (Math.abs(dragX) >= swipeDistance) {
      finalizeSwipe(dragX > 0 ? "right" : "left");
    } else {
      resetSwipe();
    }
  };

  const handleMouseLeave = () => {
    if (!pointerDownRef.current) return;
    handleMouseUp();
  };

  function advanceProgress(rel: number) {
    const card = document.getElementById("sg-swipe-active");
    if (card) {
      card.style.transform = `translate3d(${rel}px,0,0)`;
      if (!reduceMotion) {
        const p = Math.min(Math.abs(rel), swipeDistance) / swipeDistance;
        const shadowStrength = 0.15 + 0.35 * p;
        card.style.boxShadow = `0 6px 24px -6px rgba(0,0,0,${shadowStrength.toFixed(3)})`;
      }
    }
    if (Math.abs(rel) >= swipeDistance * 0.55) {
      setThresholdDir(rel > 0 ? "right" : "left");
      if (typeof navigator !== "undefined" && "vibrate" in navigator) {
        try {
          // light haptic, best effort
          (navigator as any).vibrate?.(12);
        } catch {}
      }
    } else {
      setThresholdDir(null);
    }
  }

  // Simple pull-to-refresh gesture (vertical) — optional; safe no-op if onRefresh not provided
  const pullRef = React.useRef({ y: 0, pulling: false });
  const [pullY, setPullY] = React.useState(0);
  const PULL_TRIGGER = 60;

  const onScrollTouchStart = (e: React.TouchEvent) => {
    if (!onRefresh) return;
    const t = e.touches[0];
    pullRef.current = { y: t.clientY, pulling: true };
    setPullY(0);
  };
  const onScrollTouchMove = (e: React.TouchEvent) => {
    if (!onRefresh || !pullRef.current.pulling) return;
    const dy = e.touches[0].clientY - pullRef.current.y;
    setPullY(Math.max(0, Math.min(100, dy)));
  };
  const onScrollTouchEnd = async () => {
    if (!onRefresh || !pullRef.current.pulling) return;
    const should = pullY >= PULL_TRIGGER;
    pullRef.current.pulling = false;
    setPullY(0);
    if (should) {
      try {
        await onRefresh();
      } catch {}
    }
  };

  if (!ordered.length) {
    return (
      <div className={className}>
        <div className="w-full max-w-xl mx-auto rounded border border-white/10 bg-black/30 p-4 text-sm text-white/80">
          No items to show.
          {onRefresh && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => onRefresh?.()}
                className="px-3 py-1.5 rounded border border-white/20 hover:bg-white/5"
              >
                Refresh
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{ touchAction: "pan-y" }} // allow vertical scroll on inner content
      onTouchStart={onScrollTouchStart}
      onTouchMove={onScrollTouchMove}
      onTouchEnd={onScrollTouchEnd}
    >
      {/* Pull-to-refresh indicator */}
      {onRefresh && (
        <div
          className="mx-auto mb-2 text-center text-[11px] text-white/70 transition-transform"
          style={{ transform: `translateY(${Math.min(pullY, 50)}px)` }}
        >
          {pullY >= PULL_TRIGGER ? "Release to refresh" : "Pull to refresh"}
        </div>
      )}

      <div
        id="sg-swipe-area"
        className="relative flex flex-col items-center gap-6 px-2 pb-8 w-full"
      >
        <div
          id="sg-swipe-active"
          className="w-full max-w-xl touch-pan-y select-none relative rounded-lg"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          aria-live="polite"
        >
          {/* Directional hint */}
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center z-[5]"
            aria-hidden="true"
          >
            {thresholdDir && (
              <div
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium shadow
                ${thresholdDir === "right" ? "bg-emerald-600/80 text-white" : "bg-rose-600/80 text-white"}`}
                style={{
                  transform: `scale(${0.85 + 0.15 * Math.min(Math.abs(dragX) / swipeDistance, 1)})`,
                  transition: reduceMotion ? "none" : "transform 120ms ease",
                }}
              >
                {thresholdDir === "right" ? "Favorite ★" : "Skip ⟵"}
              </div>
            )}
          </div>

          {/* Chevron controls for desktop convenience */}
          {ordered.length > 1 && (
            <button
              aria-label="Previous"
              className="absolute left-1 top-1/2 -translate-y-1/2 z-10 bg-slate-800/70 hover:bg-slate-700 text-white rounded-full w-9 h-9 flex items-center justify-center shadow"
              onClick={() => finalizeSwipe("left")}
              type="button"
            >
              ‹
            </button>
          )}
          {ordered.length > 1 && (
            <button
              aria-label="Next"
              className="absolute right-1 top-1/2 -translate-y-1/2 z-10 bg-slate-800/70 hover:bg-slate-700 text-white rounded-full w-9 h-9 flex items-center justify-center shadow"
              onClick={() => finalizeSwipe("right")}
              type="button"
            >
              ›
            </button>
          )}

          {/* Your card content */}
          {renderCard(active)}
        </div>

        {ordered.length > 1 && (
          <div className="flex items-center gap-4 mt-1 text-xs text-white/60">
            1 / {ordered.length}
          </div>
        )}

        {onRefresh && (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => onRefresh?.()}
              className="px-3 py-1.5 rounded border border-white/20 hover:bg-white/5 text-sm"
            >
              Refresh
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

